import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";

export default function HeroCarousel({ movies }: { movies: Movie[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (movies.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % movies.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  const active = movies[index];
  const go = (dir: number) => setIndex((i) => (i + dir + movies.length) % movies.length);

  return (
    <section className="relative h-[420px] w-full overflow-hidden rounded-lg bg-foreground-950 md:h-[500px]">
      {movies.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={movie.backdrop ?? movie.cover}
            alt={`${movie.title} 剧照`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/20" />
        </div>
      ))}

      <div className="pointer-events-none absolute inset-0 flex items-end">
        <div className="pointer-events-auto w-full px-5 pb-8 md:px-10 md:pb-12">
          <div key={active.id} className="max-w-2xl animate-fade-up">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                焦点推荐
              </span>
              {active.genres.slice(0, 3).map((g) => (
                <span
                  key={g}
                  className="rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur"
                >
                  {g}
                </span>
              ))}
            </div>
            <h2 className="text-2xl font-black leading-tight text-background-50 md:text-4xl">
              {active.title}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-background-100">
              <span>{active.year}</span>
              <span className="h-1 w-1 rounded-full bg-background-200" aria-hidden="true" />
              <span>导演 {active.director}</span>
              <span className="h-1 w-1 rounded-full bg-background-200" aria-hidden="true" />
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400" aria-hidden="true" />
                {active.rating.toFixed(1)}
              </span>
            </div>
            <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              {active.synopsis}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-base" aria-hidden="true" />
                查看详情
              </Link>
              <Link
                to={`/category/${active.categoryIds[0]}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-background-50/10"
              >
                <i className="ri-folder-2-line text-base" aria-hidden="true" />
                同类作品
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute right-4 top-4 flex gap-2 md:right-6 md:top-6">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一部"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors duration-200 hover:bg-background-50/35"
        >
          <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一部"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors duration-200 hover:bg-background-50/35"
        >
          <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
        </button>
      </div>

      <div className="absolute bottom-4 right-5 flex gap-1.5 md:bottom-6 md:right-10">
        {movies.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到 ${movie.title}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? "w-6 bg-primary-500" : "w-1.5 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}